import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useFavorites } from '../contexts/FavoritesContext';
import { formatMoney } from '../utils/formatters';
import { getAdTitle, getAdDescription, getAdLocation } from '../utils/adI18n';
import MarketTicker from '../components/MarketTicker';
import LoginPromptModal from '../components/LoginPromptModal';
import LocationSheet from '../components/LocationSheet';
import { useHubAuthGate } from '../components/HubAuthGate';
import { SUDAN_CROPS, getUnitLabel } from '../config/sudanCrops';
import { 
  Search, Heart, MapPin, X, ChevronRight, RefreshCw, Plus,
  Navigation, Loader2, Package, TrendingUp, BarChart3, ListFilter,
  Phone, ArrowRight, Eye
} from 'lucide-react';
import { useUnifiedSearch } from '../hooks/useUnifiedSearch';
import { toAbsoluteUrl } from '../lib/media';
import './FarmerHub.css';

// FULL OFFICIAL LIST - ALL 18 SUDAN STATES
export const SUDAN_STATES = [
  { key: 'all', name_en: 'All Sudan', name_ar: 'كل السودان' },
  { key: 'khartoum', name_en: 'Khartoum', name_ar: 'الخرطوم' },
  { key: 'gezira', name_en: 'Gezira', name_ar: 'الجزيرة' },
  { key: 'gedaref', name_en: 'Gedaref', name_ar: 'القضارف' },
  { key: 'kassala', name_en: 'Kassala', name_ar: 'كسلا' },
  { key: 'sennar', name_en: 'Sennar', name_ar: 'سنار' },
  { key: 'blue_nile', name_en: 'Blue Nile', name_ar: 'النيل الأزرق' },
  { key: 'white_nile', name_en: 'White Nile', name_ar: 'النيل الأبيض' },
  { key: 'north_kordofan', name_en: 'North Kordofan', name_ar: 'شمال كردفان' },
  { key: 'south_kordofan', name_en: 'South Kordofan', name_ar: 'جنوب كردفان' },
  { key: 'north_darfur', name_en: 'North Darfur', name_ar: 'شمال دارفور' },
  { key: 'south_darfur', name_en: 'South Darfur', name_ar: 'جنوب دارفور' },
  { key: 'east_darfur', name_en: 'East Darfur', name_ar: 'شرق دارفور' },
  { key: 'west_darfur', name_en: 'West Darfur', name_ar: 'غرب دارفور' },
  { key: 'central_darfur', name_en: 'Central Darfur', name_ar: 'وسط دارفور' },
  { key: 'river_nile', name_en: 'River Nile', name_ar: 'نهر النيل' },
  { key: 'northern', name_en: 'Northern', name_ar: 'الشمالية' },
  { key: 'red_sea', name_en: 'Red Sea', name_ar: 'البحر الأحمر' },
];

// Crop categories
const CROP_CATEGORIES = [
  { slug: 'sesame', name_en: 'Sesame', name_ar: 'سمسم', icon: '🌾', color: '#F59E0B', unit: 'jawal' },
  { slug: 'sorghum', name_en: 'Sorghum', name_ar: 'ذرة', icon: '🌽', color: '#EF4444', unit: 'quintar' },
  { slug: 'groundnuts', name_en: 'Groundnuts', name_ar: 'فول سوداني', icon: '🥜', color: '#D97706', unit: 'jawal' },
  { slug: 'onions', name_en: 'Onions', name_ar: 'بصل', icon: '🧅', color: '#8B5CF6', unit: 'crate' },
  { slug: 'cotton', name_en: 'Cotton', name_ar: 'قطن', icon: '☁️', color: '#3B82F6', unit: 'quintar' },
  { slug: 'wheat', name_en: 'Wheat', name_ar: 'قمح', icon: '🌾', color: '#F97316', unit: 'quintar' },
  { slug: 'millet', name_en: 'Millet', name_ar: 'دخن', icon: '🌾', color: '#84CC16', unit: 'quintar' },
  { slug: 'livestock', name_en: 'Livestock', name_ar: 'مواشي', icon: '🐄', color: '#10B981', unit: 'head' },
  { slug: 'farm_services', name_en: 'Farm Services', name_ar: 'خدمات زراعية', icon: '🚜', color: '#6366F1', unit: 'service' },
];

function FarmerHub() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { lang } = useLanguage();
  const { cropSlug: urlCropSlug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const isArabic = i18n.language?.startsWith('ar');
  const isRTL = document.dir === 'rtl' || isArabic;
  const navigate = useNavigate();
  
  // Hub Auth Gate - shows login modal for unauthenticated users
  const hubLang = isRTL ? 'ar' : 'en';
  const { AuthModal, WelcomeModal } = useHubAuthGate('farmer-hub', hubLang);
  
  // State
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'prices'); // 'prices' or 'ads'
  const [selectedState, setSelectedState] = useState(searchParams.get('state') || 'all');
  const [selectedCrop, setSelectedCrop] = useState(urlCropSlug || searchParams.get('crop') || null);
  const [showLocationSheet, setShowLocationSheet] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  
  // Prices state
  const [prices, setPrices] = useState([]);
  const [pricesLoading, setPricesLoading] = useState(true);
  
  // Ads state
  const [products, setProducts] = useState([]);
  const [adsLoading, setAdsLoading] = useState(false);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [searchQuery, setSearchQuery] = useState('');
  
  // Sync URL with state
  useEffect(() => {
    const params = new URLSearchParams();
    if (activeTab !== 'prices') params.set('tab', activeTab);
    if (selectedState !== 'all') params.set('state', selectedState);
    if (selectedCrop) params.set('crop', selectedCrop);
    setSearchParams(params, { replace: true });
  }, [activeTab, selectedState, selectedCrop, setSearchParams]);
  
  // Load from URL on mount
  useEffect(() => {
    if (urlCropSlug) {
      setSelectedCrop(urlCropSlug);
      setActiveTab('ads'); // Go to ads tab when a crop is in URL
    }
  }, [urlCropSlug]);

  // Fetch market prices
  const fetchPrices = useCallback(async () => {
    setPricesLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedState && selectedState !== 'all') {
        params.append('state', selectedState);
      }
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/market-prices/today?${params}`
      );
      
      if (response.ok) {
        const data = await response.json();
        setPrices(data.prices || []);
      } else {
        // Generate mock prices if endpoint fails
        generateMockPrices();
      }
    } catch (error) {
      console.error('Failed to fetch prices:', error);
      generateMockPrices();
    } finally {
      setPricesLoading(false);
    }
  }, [selectedState]);
  
  // Generate mock prices for demo
  const generateMockPrices = () => {
    const mockPrices = CROP_CATEGORIES.filter(c => c.slug !== 'farm_services').map(crop => ({
      cropSlug: crop.slug,
      cropNameAr: crop.name_ar,
      cropNameEn: crop.name_en,
      avgPrice: Math.floor(Math.random() * 50000) + 15000,
      unit: crop.unit,
      icon: crop.icon,
      trend: Math.random() > 0.5 ? 'up' : 'down',
      changePercent: (Math.random() * 10).toFixed(1),
    }));
    setPrices(mockPrices);
  };
  
  // Fetch crop ads
  const fetchAds = useCallback(async () => {
    setAdsLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('category', 'agriculture');
      params.append('limit', '30');
      
      if (selectedCrop) {
        params.append('crop_slug', selectedCrop);
      }
      if (selectedState && selectedState !== 'all') {
        params.append('state', selectedState);
      }
      if (searchQuery) {
        params.append('search', searchQuery);
      }
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/ads?${params}`
      );
      
      if (response.ok) {
        const data = await response.json();
        setProducts(data.ads || []);
      }
    } catch (error) {
      console.error('Failed to fetch ads:', error);
    } finally {
      setAdsLoading(false);
    }
  }, [selectedCrop, selectedState, searchQuery]);
  
  // Load data on mount and state changes
  useEffect(() => {
    fetchPrices();
  }, [fetchPrices]);
  
  useEffect(() => {
    if (activeTab === 'ads') {
      fetchAds();
    }
  }, [activeTab, fetchAds]);
  
  // Load favorites
  useEffect(() => {
  }, []);

  // ═══════════════════════════════════════════════════════════
  // UNIFIED SEARCH PIPELINE — single source of truth
  // normalize → filter → dedupe (shared with Home, Shop, etc.)
  // ═══════════════════════════════════════════════════════════
  const displayProducts = useUnifiedSearch(products, searchQuery);

  // Handle crop selection from prices tab - navigate to ads
  const handleCropSelect = (cropSlug) => {
    setSelectedCrop(cropSlug);
    setActiveTab('ads');
  };
  
  // Clear crop filter
  const clearCropFilter = () => {
    setSelectedCrop(null);
  };
  
  // Get selected state display name
  const getStateName = () => {
    const state = SUDAN_STATES.find(s => s.key === selectedState);
    return state ? (isArabic ? state.name_ar : state.name_en) : '';
  };
  
  // Get selected crop info
  const getSelectedCropInfo = () => {
    return CROP_CATEGORIES.find(c => c.slug === selectedCrop);
  };
  
  // Handle location selection
  const handleLocationSelect = (stateKey) => {
    setSelectedState(stateKey);
    setShowLocationSheet(false);
  };
  
  // Handle ad click
  const handleAdClick = (ad) => {
    navigate(`/ad/${ad.ad_id}`);
  };
  
  // Handle post ad
  const handlePostAd = () => {
    if (!user) {
      setShowLoginPrompt(true);
      return;
    }
    navigate('/add-ad?category=agriculture' + (selectedCrop ? `&crop=${selectedCrop}` : ''));
  };
  
  // Image helper — iter275: delegate to canonical resolver
  const getImageUrl = (imagePath) => {
    if (!imagePath) return '/placeholder.svg';
    return toAbsoluteUrl(imagePath);
  };

  return (
    <div className="farmer-hub" dir={isRTL ? 'rtl' : 'ltr'} data-testid="farmer-hub">
      {/* Hub Auth Gate - Login/Signup Modal for unauthenticated users */}
      <AuthModal />
      <WelcomeModal />
      
      {/* HEADER SECTION */}
      <div className="farmer-hub-header">
        {/* Market Ticker */}
        <MarketTicker 
          state={selectedState} 
          onTickerClick={() => setActiveTab('prices')}
        />
        
        {/* Title & Location Selector */}
        <div className="farmer-hub-title-row">
          <h1 className="farmer-hub-title">
            {isArabic ? '🌾 مركز الزراعة' : '🌾 Farmer Hub'}
          </h1>
          <button 
            className="location-selector"
            onClick={() => setShowLocationSheet(true)}
            data-testid="location-selector"
          >
            <MapPin size={16} />
            <span>{getStateName()}</span>
            <ChevronRight size={16} className="chevron" />
          </button>
        </div>
      </div>
      
      {/* TAB NAVIGATION */}
      <div className="farmer-hub-tabs" data-testid="farmer-hub-tabs">
        <button
          className={`hub-tab ${activeTab === 'prices' ? 'active' : ''}`}
          onClick={() => setActiveTab('prices')}
          data-testid="tab-prices"
        >
          <BarChart3 size={18} />
          <span>{isArabic ? 'الأسعار' : 'Prices'}</span>
        </button>
        <button
          className={`hub-tab ${activeTab === 'ads' ? 'active' : ''}`}
          onClick={() => setActiveTab('ads')}
          data-testid="tab-ads"
        >
          <ListFilter size={18} />
          <span>{isArabic ? 'الإعلانات' : 'Ads'}</span>
          {selectedCrop && (
            <span className="tab-badge">{getSelectedCropInfo()?.icon}</span>
          )}
        </button>
      </div>
      
      {/* CONTENT AREA */}
      <div className="farmer-hub-content">
        
        {/* ========== PRICES TAB ========== */}
        {activeTab === 'prices' && (
          <div className="prices-tab" data-testid="prices-content">
            <div className="prices-header">
              <h2 className="section-title">
                {isArabic ? 'أسعار اليوم' : "Today's Prices"}
              </h2>
              <button 
                className="refresh-btn"
                onClick={fetchPrices}
                disabled={pricesLoading}
              >
                <RefreshCw size={16} className={pricesLoading ? 'spinning' : ''} />
              </button>
            </div>
            
            {pricesLoading ? (
              <div className="loading-state">
                <Loader2 size={32} className="spinning" />
                <p>{isArabic ? 'جارٍ تحميل الأسعار...' : 'Loading prices...'}</p>
              </div>
            ) : (
              <div className="prices-list">
                {prices.map((price) => (
                  <button
                    key={price.cropSlug}
                    className={`price-row ${selectedCrop === price.cropSlug ? 'selected' : ''}`}
                    onClick={() => handleCropSelect(price.cropSlug)}
                    data-testid={`price-${price.cropSlug}`}
                  >
                    <div className="price-crop-info">
                      <span className="crop-icon">{price.icon || '🌾'}</span>
                      <div className="crop-details">
                        <span className="crop-name">
                          {isArabic ? price.cropNameAr : price.cropNameEn}
                        </span>
                        <span className="crop-unit">
                          {isArabic ? `لكل ${getUnitLabel(price.unit, 'ar')}` : `per ${getUnitLabel(price.unit, 'en')}`}
                        </span>
                      </div>
                    </div>
                    <div className="price-value-area">
                      <span className="price-value">
                        {formatMoney(price.avgPrice, 'SDG')}
                      </span>
                      {price.trend && (
                        <span className={`price-trend ${price.trend}`}>
                          {price.trend === 'up' ? '↑' : '↓'} {price.changePercent}%
                        </span>
                      )}
                    </div>
                    <div className="view-ads-hint">
                      <Eye size={14} />
                      <span>{isArabic ? 'عرض الإعلانات' : 'View Ads'}</span>
                      <ArrowRight size={14} />
                    </div>
                  </button>
                ))}
              </div>
            )}
            
            {/* Crop Categories Grid - Quick Access */}
            <div className="crop-categories-section">
              <h3 className="subsection-title">
                {isArabic ? 'تصفح حسب المحصول' : 'Browse by Crop'}
              </h3>
              <div className="crop-grid">
                {CROP_CATEGORIES.map((crop) => (
                  <button
                    key={crop.slug}
                    className={`crop-card ${selectedCrop === crop.slug ? 'selected' : ''}`}
                    onClick={() => handleCropSelect(crop.slug)}
                    style={{ '--crop-color': crop.color }}
                    data-testid={`crop-${crop.slug}`}
                  >
                    <span className="crop-card-icon">{crop.icon}</span>
                    <span className="crop-card-name">
                      {isArabic ? crop.name_ar : crop.name_en}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
        
        {/* ========== ADS TAB ========== */}
        {activeTab === 'ads' && (
          <div className="ads-tab" data-testid="ads-content">
            {/* Active Crop Filter */}
            {selectedCrop && (
              <div className="active-crop-filter">
                <div className="filter-info">
                  <span className="filter-icon">{getSelectedCropInfo()?.icon}</span>
                  <span className="filter-name">
                    {isArabic ? getSelectedCropInfo()?.name_ar : getSelectedCropInfo()?.name_en}
                  </span>
                </div>
                <button 
                  className="clear-filter-btn"
                  onClick={clearCropFilter}
                  data-testid="clear-crop-filter"
                >
                  <X size={16} />
                </button>
              </div>
            )}
            
            {/* Search & Actions Bar */}
            <div className="ads-toolbar">
              <div className="search-input-wrapper">
                <Search size={18} className="search-icon" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={isArabic ? 'ابحث في الإعلانات...' : 'Search ads...'}
                  className="search-input"
                  data-testid="ads-search"
                />
              </div>
              <button 
                className="post-ad-btn"
                onClick={handlePostAd}
                data-testid="post-ad-btn"
              >
                <Plus size={18} />
                <span>{isArabic ? 'أضف إعلان' : 'Post Ad'}</span>
              </button>
            </div>
            
            {/* Crop Quick Filters (horizontal scroll) */}
            <div className="crop-quick-filters">
              <button
                className={`quick-filter ${!selectedCrop ? 'active' : ''}`}
                onClick={() => setSelectedCrop(null)}
              >
                {isArabic ? 'الكل' : 'All'}
              </button>
              {CROP_CATEGORIES.map((crop) => (
                <button
                  key={crop.slug}
                  className={`quick-filter ${selectedCrop === crop.slug ? 'active' : ''}`}
                  onClick={() => setSelectedCrop(crop.slug)}
                >
                  <span>{crop.icon}</span>
                  <span>{isArabic ? crop.name_ar : crop.name_en}</span>
                </button>
              ))}
            </div>
            
            {/* Ads Grid */}
            {adsLoading ? (
              <div className="loading-state">
                <Loader2 size={32} className="spinning" />
                <p>{isArabic ? 'جارٍ تحميل الإعلانات...' : 'Loading ads...'}</p>
              </div>
            ) : displayProducts.length === 0 ? (
              <div className="empty-state">
                <Package size={48} strokeWidth={1.5} />
                <h3>{isArabic ? 'لا توجد إعلانات' : 'No Ads Yet'}</h3>
                <p>
                  {selectedCrop 
                    ? (isArabic ? `لا توجد إعلانات ${getSelectedCropInfo()?.name_ar} حالياً` : `No ${getSelectedCropInfo()?.name_en} ads yet`)
                    : (isArabic ? 'كن أول من ينشر إعلان!' : 'Be the first to post!')
                  }
                </p>
                <button className="cta-btn" onClick={handlePostAd}>
                  <Plus size={18} />
                  {isArabic ? 'أنشر إعلانك الآن' : 'Post Your Ad Now'}
                </button>
              </div>
            ) : (
              <div className="ads-grid">
                {displayProducts.map((ad) => (
                  <div
                    key={ad.ad_id}
                    className="ad-card"
                    onClick={() => handleAdClick(ad)}
                    data-testid={`ad-${ad.ad_id}`}
                  >
                    {ad.isSample && (
                      <span className="sample-badge">
                        {isArabic ? 'نموذج' : 'Sample'}
                      </span>
                    )}
                    <div className="ad-image-wrapper">
                      {ad.images && ad.images.length > 0 ? (
                        <img
                          src={getImageUrl(ad.images[0])}
                          alt={getAdTitle(ad, lang).text || ad.title}
                          className="ad-image"
                          onError={(e) => { e.target.src = '/placeholder.svg'; }}
                        />
                      ) : (
                        <div className="ad-placeholder">
                          <Package size={32} />
                        </div>
                      )}
                      <button
                        className={`favorite-btn ${isFavorite(ad.ad_id) ? 'active' : ''}`}
                        onClick={(e) => toggleFavorite(ad.ad_id, e)}
                      >
                        <Heart size={14} fill={isFavorite(ad.ad_id) ? 'currentColor' : 'none'} />
                      </button>
                    </div>
                    <div className="ad-info">
                      <h3 className="ad-title">{getAdTitle(ad, lang).text || ad.title}</h3>
                      <p className="ad-price">
                        {formatMoney(ad.price, ad.currency || 'SDG')}
                      </p>
                      <div className="ad-meta">
                        <span className="ad-location">
                          <MapPin size={12} />
                          {getAdLocation(ad, lang)}
                        </span>
                        {ad.crop_slug && (
                          <span className="ad-crop-tag">
                            {CROP_CATEGORIES.find(c => c.slug === ad.crop_slug)?.icon}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* LOCATION SHEET */}
      <LocationSheet
        isOpen={showLocationSheet}
        onClose={() => setShowLocationSheet(false)}
        selectedState={selectedState}
        onSelectState={handleLocationSelect}
        states={SUDAN_STATES}
      />
      
      {/* LOGIN PROMPT */}
      <LoginPromptModal
        isOpen={showLoginPrompt}
        onClose={() => setShowLoginPrompt(false)}
        action="post-ad"
      />
    </div>
  );
}

export default FarmerHub;
